import { useInView } from "@/hooks/useInView";
import { Ban, FileCheck, Clock4, Handshake, Scale, Unlock } from "lucide-react";

const principles = [
  {
    icon: Ban,
    title: "Najpierw mówimy „nie”",
    text: "Jeśli Twoja lokalizacja nie ma realnych szans na realizację, powiemy to wprost — w pierwszej rozmowie, zanim zainwestujesz w to czas. Nie sprzedajemy nadziei.",
    color: "emerald",
  },
  {
    icon: Scale,
    title: "Umowa do skonsultowania z prawnikiem",
    text: "Każdy projekt umowy dzierżawy możesz wziąć do swojego prawnika przed podpisem. Bez presji, bez „dziś albo nigdy”. Nasz interes to długoletnia współpraca, nie wyciśnięcie podpisu.",
    color: "emerald",
  },
  {
    icon: Clock4,
    title: "Pierwszy czynsz — gdy stoi koparka",
    text: "Nie pobieramy zaliczek, ale i my nie płacimy od dnia podpisania umowy. Pierwszy czynsz wpływa, gdy rozpoczynają się prace budowlane — to uczciwy układ dla obu stron.",
    color: "amber",
  },
  {
    icon: FileCheck,
    title: "Mówimy o terminach realnie",
    text: "Pozwolenia trwają 8–24 miesiące — i tyle Ci powiemy. Jeśli ktoś obiecuje budowę „za pół roku”, kłamie. Wiemy, jak działają polskie urzędy, bo z nimi pracujemy codziennie.",
    color: "emerald",
  },
  {
    icon: Handshake,
    title: "Bez agresywnej sprzedaży",
    text: "Po zostawieniu kontaktu dzwonimy raz — przedstawiamy ofertę. Jeśli się nie zdecydujesz, nie nękamy telefonami ani SMS-ami. Twoja decyzja, Twój czas.",
    color: "amber",
  },
  {
    icon: Unlock,
    title: "Nie żądamy wyłączności",
    text: "Możesz równolegle rozmawiać z innymi firmami z branży — nie zobowiązujemy Cię do wyłączności na etapie negocjacji. Wybierasz ofertę, która Ci pasuje, nie tę, w którą Cię ktoś wciągnął.",
    color: "emerald",
  },
];

export default function OurPrinciples() {
  const [ref, inView] = useInView();

  return (
    <section
      id="zasady"
      className="py-24 bg-white"
      data-testid="our-principles-section"
    >
      <div className="max-w-7xl mx-auto px-6">
        <div
          ref={ref}
          className={`text-center mb-16 transition-all duration-700 ${
            inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <span className="text-emerald-700 text-sm font-semibold uppercase tracking-widest">
            Nasze zasady
          </span>
          <h2 className="text-4xl sm:text-5xl font-black text-slate-900 mt-2 tracking-tight">
            Jak współpracujemy z partnerami
          </h2>
          <p className="text-slate-600 text-lg mt-4 max-w-2xl mx-auto">
            Branża, w której działamy, ma reputację agresywnej sprzedaży i obietnic bez pokrycia.
            Pracujemy inaczej — oto sześć konkretnych zasad, od których nie odstępujemy.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {principles.map((p, i) => (
            <PrincipleCard key={p.title} principle={p} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function PrincipleCard({ principle, index }) {
  const [ref, inView] = useInView();
  const Icon = principle.icon;
  const isAmber = principle.color === "amber";

  return (
    <div
      ref={ref}
      className={`bg-slate-50 rounded-2xl p-6 border border-slate-200 hover:shadow-lg hover:shadow-emerald-900/5 hover:-translate-y-1 transition-all duration-700 ${
        inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      }`}
      style={{ transitionDelay: `${index * 100}ms` }}
      data-testid={`principle-card-${index + 1}`}
    >
      <div
        className={`w-11 h-11 rounded-xl flex items-center justify-center mb-4 ${
          isAmber ? "bg-amber-100" : "bg-emerald-100"
        }`}
      >
        <Icon
          className={`w-5 h-5 ${isAmber ? "text-amber-700" : "text-emerald-700"}`}
          aria-hidden="true"
        />
      </div>
      <h3 className="font-bold text-slate-900 text-base mb-2 leading-tight">
        {principle.title}
      </h3>
      <p className="text-slate-600 text-sm leading-relaxed">{principle.text}</p>
    </div>
  );
}
